"use client";

import { useEffect, useState } from "react";
import Pusher from "pusher-js";
import { useToast } from "@/hooks/use-toast";

// Initialize Pusher
let pusherClient: Pusher | null = null;

export interface FleetHubUpdate {
  action: "add" | "remove";
  itemId: string;
  itemType: "product" | "vehicle";
  userId: string;
}

export interface UseFleetHubUpdatesProps {
  companyId: string | null;
  onUpdate?: (update: FleetHubUpdate) => void;
  showToasts?: boolean;
}

export function useFleetHubUpdates({
  companyId,
  onUpdate,
  showToasts = true,
}: UseFleetHubUpdatesProps) {
  const [isConnected, setIsConnected] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Skip if no company ID
    if (!companyId) {
      return;
    }

    // Initialize Pusher client if not already initialized
    if (!pusherClient) {
      pusherClient = new Pusher(process.env.NEXT_PUBLIC_PUSHER_KEY!, {
        cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER!,
      });
    }

    // Subscribe to the company channel
    const channelName = `company-${companyId}`;
    const channel = pusherClient.subscribe(channelName);

    // Set up event handler for FleetHub updates
    channel.bind("fleethub-update", (data: FleetHubUpdate) => {
      // Call the onUpdate callback if provided
      if (onUpdate) {
        onUpdate(data);
      }

      // Show toast notification if enabled
      if (showToasts) {
        toast({
          title:
            data.action === "add"
              ? "Item added to company FleetHub"
              : "Item removed from company FleetHub",
          description: `A colleague has ${
            data.action === "add" ? "added an" : "removed an"
          } item ${data.action === "add" ? "to" : "from"} your company's FleetHub.`,
        });
      }
    });

    // Set connection status
    setIsConnected(true);

    // Clean up on unmount
    return () => {
      channel.unbind_all();
      pusherClient?.unsubscribe(channelName);
    };
  }, [companyId, onUpdate, showToasts, toast]);

  return { isConnected };
}
